/**
 * Lane B async queue commands — sherwood queue <subcommand>
 *
 * During an active proposal the vault locks instant deposit/withdraw. Lane B is
 * the universal async path: `requestDeposit` / `requestRedeem` escrow into the
 * per-vault VaultWithdrawalQueue tagged to the active proposal id (pid). At
 * settle the governor stamps ONE frozen price for that pid; anyone then `claim`s
 * at that single batched price. `cancel` is only allowed before the pid is
 * stamped (Lane B G7) — it reverts once stamped.
 *
 * User-facing entry points (requestDeposit/requestRedeem) live on the VAULT;
 * claim/cancel/inspection live on the QUEUE, reached via `vault.withdrawalQueue()`.
 */

import { Command } from "commander";
import type { Address } from "viem";
import { parseUnits, formatUnits, parseEther, isAddress, parseEventLogs } from "viem";
import chalk from "chalk";
import ora from "ora";
import {
  getPublicClient,
  getAccount,
  formatContractError,
  writeContractWithRetry,
  waitForReceipt,
} from "../lib/client.js";
import { getChain, getExplorerUrl } from "../lib/network.js";
import { ERC20_ABI } from "../lib/abis.js";
import { TOKENS } from "../lib/addresses.js";

// Minimal WETH9 fragment — payable deposit() for the --use-eth wrap leg.
const WETH_ABI = [
  { name: "deposit", type: "function", stateMutability: "payable", inputs: [], outputs: [] },
] as const;

/** Read the vault asset + its ERC20 decimals live (no 6-dec assumption). */
async function getVaultAsset(vault: Address): Promise<{ asset: Address; decimals: number }> {
  const client = getPublicClient();
  const asset = (await client.readContract({
    address: vault,
    abi: VAULT_QUEUE_ABI,
    functionName: "asset",
  })) as Address;
  const decimals = (await client.readContract({
    address: asset,
    abi: ERC20_ABI,
    functionName: "decimals",
  })) as number;
  return { asset, decimals };
}

// ── ABI fragments (transcribed from compiled SyndicateVault / VaultWithdrawalQueue artifacts) ──

const VAULT_QUEUE_ABI = [
  {
    name: "requestDeposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "assets", type: "uint256" },
      { name: "receiver", type: "address" },
    ],
    outputs: [{ name: "requestId", type: "uint256" }],
  },
  {
    name: "requestRedeem",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "shares", type: "uint256" },
      { name: "owner_", type: "address" },
    ],
    outputs: [{ name: "requestId", type: "uint256" }],
  },
  {
    name: "withdrawalQueue",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "asset",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    type: "event",
    name: "DepositRequested",
    inputs: [
      { name: "requestId", type: "uint256", indexed: true },
      { name: "receiver", type: "address", indexed: true },
      { name: "assets", type: "uint256", indexed: false },
    ],
  },
  {
    type: "event",
    name: "RedeemRequested",
    inputs: [
      { name: "requestId", type: "uint256", indexed: true },
      { name: "owner", type: "address", indexed: true },
      { name: "shares", type: "uint256", indexed: false },
    ],
  },
] as const;

const QUEUE_ABI = [
  {
    name: "claim",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "requestId", type: "uint256" }],
    outputs: [{ name: "outAmount", type: "uint256" }],
  },
  {
    name: "cancel",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "requestId", type: "uint256" }],
    outputs: [],
  },
  {
    name: "getRequestsByOwner",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "owner_", type: "address" }],
    outputs: [{ name: "", type: "uint256[]" }],
  },
  {
    name: "getRequest",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "id", type: "uint256" }],
    outputs: [
      {
        name: "",
        type: "tuple",
        components: [
          { name: "owner", type: "address" },
          { name: "amount", type: "uint256" },
          { name: "pid", type: "uint256" },
          { name: "kind", type: "uint8" },
          { name: "claimed", type: "bool" },
          { name: "cancelled", type: "bool" },
        ],
      },
    ],
  },
  {
    name: "getSettlePrice",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "pid", type: "uint256" }],
    outputs: [
      {
        name: "",
        type: "tuple",
        components: [
          { name: "num", type: "uint256" },
          { name: "den", type: "uint256" },
          { name: "stamped", type: "bool" },
        ],
      },
    ],
  },
] as const;

// RequestKind enum ordering from src/interfaces/IVaultWithdrawalQueue.sol:
//   0 = Redeem (escrow shares, claim assets), 1 = Deposit (escrow assets, claim shares).
const KIND_LABELS = ["Redeem", "Deposit"] as const;

type QueueRequest = {
  owner: Address;
  amount: bigint;
  pid: bigint;
  kind: number;
  claimed: boolean;
  cancelled: boolean;
};

/**
 * Parse a request id out of a command's OWN printed output (used by scenario s04).
 * Matches `Queued redeem request #12 (pid 3)` and `requestId: 7`.
 */
export function parseRequestId(output: string): bigint | undefined {
  const m = output.match(/request\s*#?(\d+)/i) ?? output.match(/requestId[:\s]+(\d+)/i);
  return m ? BigInt(m[1]) : undefined;
}

function resolveVaultAddress(opt: string | undefined): Address {
  if (!opt || !isAddress(opt)) {
    console.error(chalk.red(`Invalid or missing --vault address: ${opt ?? "(none)"}`));
    process.exit(1);
  }
  return opt as Address;
}

async function getQueueAddress(vault: Address): Promise<Address> {
  const client = getPublicClient();
  return (await client.readContract({
    address: vault,
    abi: VAULT_QUEUE_ABI,
    functionName: "withdrawalQueue",
  })) as Address;
}

async function readRequest(queue: Address, id: bigint): Promise<QueueRequest> {
  const client = getPublicClient();
  return (await client.readContract({
    address: queue,
    abi: QUEUE_ABI,
    functionName: "getRequest",
    args: [id],
  })) as QueueRequest;
}

export function registerQueueCommands(program: Command): void {
  const queue = program
    .command("queue")
    .description("Lane B async deposit/redeem queue (during proposals)");

  // ── queue request-deposit ──

  queue
    .command("request-deposit")
    .description(
      "Queue a Lane B deposit (escrows the vault asset; settles at the frozen per-proposal price)",
    )
    .requiredOption("--vault <address>", "Vault address")
    .requiredOption("--amount <amount>", "Amount to deposit (in the vault asset's units)")
    .option("--use-eth", "Auto-wrap ETH → WETH before queueing (for WETH vaults)")
    .action(async (opts: { vault: string; amount: string; useEth?: boolean }) => {
      const vault = resolveVaultAddress(opts.vault);
      const account = getAccount();
      const client = getPublicClient();

      const spinner = ora(`Queueing deposit of ${opts.amount}...`).start();
      try {
        // Resolve the asset + its decimals live — never assume USDC-6 (the
        // Robinhood-testnet vault asset is an 18-dec WETH-family token).
        const { asset, decimals } = await getVaultAsset(vault);
        const assets = parseUnits(opts.amount, decimals);

        // --use-eth: wrap the shortfall of native ETH into the WETH asset first
        // (mirrors `vault deposit --use-eth` / vault.ts depositWithEthWrap).
        if (opts.useEth) {
          if (asset.toLowerCase() !== TOKENS().WETH.toLowerCase()) {
            throw new Error("--use-eth is only supported for WETH vaults.");
          }
          const wethBalance = (await client.readContract({
            address: asset,
            abi: ERC20_ABI,
            functionName: "balanceOf",
            args: [account.address],
          })) as bigint;
          if (wethBalance < assets) {
            const shortfall = assets - wethBalance;
            const ethBalance = await client.getBalance({ address: account.address });
            const minGas = parseEther("0.0005");
            if (ethBalance < shortfall + minGas) {
              throw new Error(
                `Insufficient ETH. Have ${formatUnits(ethBalance, 18)} ETH, need ${formatUnits(shortfall, 18)} to wrap + gas.`,
              );
            }
            spinner.text = "Wrapping ETH → WETH...";
            const wrapHash = await writeContractWithRetry({
              account,
              chain: getChain(),
              address: asset,
              abi: WETH_ABI,
              functionName: "deposit",
              args: [],
              value: shortfall,
            });
            await waitForReceipt(wrapHash);
          }
        }

        // Approve the asset to the vault if the existing allowance is short (mirror the deposit flow).
        const currentAllowance = (await client.readContract({
          address: asset,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [account.address, vault],
        })) as bigint;

        if (currentAllowance < assets) {
          spinner.text = "Approving asset to vault...";
          const approveHash = await writeContractWithRetry({
            account,
            chain: getChain(),
            address: asset,
            abi: ERC20_ABI,
            functionName: "approve",
            args: [vault, assets],
          });
          await waitForReceipt(approveHash);
        }

        spinner.text = `Queueing deposit of ${opts.amount}...`;
        const hash = await writeContractWithRetry({
          account,
          chain: getChain(),
          address: vault,
          abi: VAULT_QUEUE_ABI,
          functionName: "requestDeposit",
          args: [assets, account.address],
        });
        const receipt = await waitForReceipt(hash);

        const logs = parseEventLogs({
          abi: VAULT_QUEUE_ABI,
          eventName: "DepositRequested",
          logs: receipt.logs,
        });
        const id = logs[0]?.args?.requestId as bigint | undefined;
        if (id === undefined) {
          spinner.fail("Deposit queued but could not read requestId from logs");
          console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
          process.exit(1);
        }

        const req = await readRequest(await getQueueAddress(vault), id);
        spinner.succeed(`Queued deposit request #${id} (pid ${req.pid})`);
        // ora writes .succeed() to stderr — also print to stdout so stdout-only
        // parsers (e.g. the e2e harness's parseRequestId) can read the request id.
        console.log(`Queued deposit request #${id} (pid ${req.pid})`);
        console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
      } catch (err) {
        spinner.fail("Queue deposit failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── queue request-redeem ──

  queue
    .command("request-redeem")
    .description("Queue a Lane B redeem (escrows shares; settles at the frozen per-proposal price)")
    .requiredOption("--vault <address>", "Vault address")
    .requiredOption("--shares <shares>", "Share amount (raw integer, 12 decimals for USDC vaults)")
    .action(async (opts: { vault: string; shares: string }) => {
      const vault = resolveVaultAddress(opts.vault);
      const account = getAccount();
      const shares = BigInt(opts.shares); // raw shares — do NOT parseUnits

      const spinner = ora(`Queueing redeem of ${opts.shares} shares...`).start();
      try {
        const hash = await writeContractWithRetry({
          account,
          chain: getChain(),
          address: vault,
          abi: VAULT_QUEUE_ABI,
          functionName: "requestRedeem",
          args: [shares, account.address],
        });
        const receipt = await waitForReceipt(hash);

        const logs = parseEventLogs({
          abi: VAULT_QUEUE_ABI,
          eventName: "RedeemRequested",
          logs: receipt.logs,
        });
        const id = logs[0]?.args?.requestId as bigint | undefined;
        if (id === undefined) {
          spinner.fail("Redeem queued but could not read requestId from logs");
          console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
          process.exit(1);
        }

        const req = await readRequest(await getQueueAddress(vault), id);
        spinner.succeed(`Queued redeem request #${id} (pid ${req.pid})`);
        // ora writes .succeed() to stderr — also print to stdout so stdout-only
        // parsers (e.g. the e2e harness's parseRequestId) can read the request id.
        console.log(`Queued redeem request #${id} (pid ${req.pid})`);
        console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
      } catch (err) {
        spinner.fail("Queue redeem failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── queue claim ──

  queue
    .command("claim")
    .description("Claim a settled Lane B request at the frozen per-proposal price")
    .requiredOption("--vault <address>", "Vault address")
    .requiredOption("--id <requestId>", "Request id to claim")
    .action(async (opts: { vault: string; id: string }) => {
      const vault = resolveVaultAddress(opts.vault);
      const account = getAccount();
      const id = BigInt(opts.id);

      const spinner = ora(`Claiming request #${id}...`).start();
      try {
        const queueAddr = await getQueueAddress(vault);
        const hash = await writeContractWithRetry({
          account,
          chain: getChain(),
          address: queueAddr,
          abi: QUEUE_ABI,
          functionName: "claim",
          args: [id],
        });
        const receipt = await waitForReceipt(hash);
        // outAmount is the call return, not an event field; re-read the request to confirm claimed.
        const req = await readRequest(queueAddr, id);
        const label = KIND_LABELS[req.kind] ?? `kind ${req.kind}`;
        const unit = req.kind === 1 ? "shares" : "assets";
        void receipt;
        spinner.succeed(`Claimed ${label} request #${id} → received ${unit}`);
        console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
      } catch (err) {
        spinner.fail("Claim failed");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── queue cancel ──

  queue
    .command("cancel")
    .description("Cancel a pending Lane B request (only before the pid is stamped — G7)")
    .requiredOption("--vault <address>", "Vault address")
    .requiredOption("--id <requestId>", "Request id to cancel")
    .action(async (opts: { vault: string; id: string }) => {
      const vault = resolveVaultAddress(opts.vault);
      const account = getAccount();
      const id = BigInt(opts.id);

      const spinner = ora(`Cancelling request #${id}...`).start();
      try {
        const queueAddr = await getQueueAddress(vault);
        const hash = await writeContractWithRetry({
          account,
          chain: getChain(),
          address: queueAddr,
          abi: QUEUE_ABI,
          functionName: "cancel",
          args: [id],
        });
        await waitForReceipt(hash);
        spinner.succeed(`Cancelled request #${id}`);
        console.log(chalk.dim(`  ${getExplorerUrl(hash)}`));
      } catch (err) {
        // Reverts once the pid is stamped (G7) — surface the reason rather than crashing.
        spinner.fail("Cancel failed (request may already be settled — cancel is only allowed before the price is stamped)");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });

  // ── queue list ──

  queue
    .command("list")
    .description("List the caller's Lane B requests")
    .requiredOption("--vault <address>", "Vault address")
    .action(async (opts: { vault: string }) => {
      const vault = resolveVaultAddress(opts.vault);
      const account = getAccount();
      const client = getPublicClient();

      const spinner = ora("Loading queue requests...").start();
      try {
        const queueAddr = await getQueueAddress(vault);
        const ids = (await client.readContract({
          address: queueAddr,
          abi: QUEUE_ABI,
          functionName: "getRequestsByOwner",
          args: [account.address],
        })) as bigint[];

        if (ids.length === 0) {
          spinner.stop();
          console.log(chalk.dim("  No queue requests for this address."));
          return;
        }

        const requests = await Promise.all(ids.map((id) => readRequest(queueAddr, id)));
        spinner.stop();

        console.log();
        console.log(chalk.bold(`Queue requests (${ids.length}) — ${account.address}`));
        console.log(chalk.dim("─".repeat(72)));
        console.log(
          chalk.dim(
            `  ${"id".padEnd(6)}${"kind".padEnd(9)}${"amount".padEnd(26)}${"pid".padEnd(6)}${"claimed".padEnd(9)}cancelled`,
          ),
        );
        ids.forEach((id, i) => {
          const r = requests[i];
          const label = KIND_LABELS[r.kind] ?? `kind ${r.kind}`;
          console.log(
            `  ${String(id).padEnd(6)}${label.padEnd(9)}${String(r.amount).padEnd(26)}${String(r.pid).padEnd(6)}${String(r.claimed).padEnd(9)}${r.cancelled}`,
          );
        });
        console.log();
      } catch (err) {
        spinner.fail("Failed to load queue requests");
        console.error(chalk.red(formatContractError(err)));
        process.exit(1);
      }
    });
}
